import { useEffect, useRef, useState } from 'react';
import { loadKakaoMaps } from '../../lib/kakaoMaps';
import './LocationPicker.css';
import { LocationPreviewMap } from './LocationPreviewMap';

// calendar-location-picker (2026-09-02) — "외부일정일 때 장소도 카카오 맵으로 받을 수 있게
// 해줘" 요청. 자유 텍스트 입력은 그대로 두고(기존 동작 보존 — 검색이 실패하거나 원하는
// 장소가 안 나와도 손으로 적을 수 있어야 한다), 타이핑에 맞춰 kakao.maps.services.Places
// 키워드 검색 결과를 드롭다운으로 보여준다. 결과를 고르면 "장소명 (도로명주소)" 형태로
// location 문자열을 채운다 — 백엔드는 이 문자열을 그대로 kakao-geo.ts 로 지오코딩해
// 이동시간을 계산하므로(TravelTimeResponse), 좌표를 별도로 서버에 보낼 필요가 없다.
// 선택 후에는 확인용 미리보기 지도를 보여준다(2026-11-02 부터 끌고 확대할 수 있다 —
// `LocationPreviewMap`).

interface KakaoPlace {
  id: string;
  place_name: string;
  address_name: string;
  road_address_name: string;
  x: string; // lng
  y: string; // lat
}

interface LocationPickerProps {
  id?: string;
  value: string;
  onChange: (location: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function LocationPicker({ id, value, onChange, placeholder, disabled }: LocationPickerProps) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<KakaoPlace[]>([]);
  const [selectedPos, setSelectedPos] = useState<{ lat: number; lng: number } | null>(null);
  const placesRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    loadKakaoMaps()
      .then(() => {
        if (cancelled) return;
        placesRef.current = new window.kakao.maps.services.Places();
      })
      .catch(() => { /* 검색 SDK 로드 실패 — 자유 텍스트 입력은 여전히 동작하므로 조용히 무시. */ });
    return () => { cancelled = true; };
  }, []);

  // 바깥 클릭 시 결과 드롭다운을 닫는다(콤보박스 관용 동작).
  useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  // 타이핑에 맞춰 검색(300ms debounce) — 카카오 키워드 검색 API.
  useEffect(() => {
    const q = value.trim();
    if (!q || !placesRef.current) { setResults([]); return; }
    const handle = window.setTimeout(() => {
      placesRef.current.keywordSearch(q, (data: KakaoPlace[], status: string) => {
        if (status === window.kakao?.maps?.services?.Status?.OK) setResults(data.slice(0, 6));
        else setResults([]);
      });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [value]);

  function handleSelect(place: KakaoPlace) {
    const addr = place.road_address_name || place.address_name;
    const label = place.place_name && addr ? `${place.place_name} (${addr})` : (place.place_name || addr);
    onChange(label);
    setSelectedPos({ lat: Number(place.y), lng: Number(place.x) });
    setResults([]);
    setOpen(false);
  }

  return (
    <div className="calendar-location-picker" ref={containerRef}>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setSelectedPos(null); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        maxLength={200}
        disabled={disabled}
        autoComplete="off"
      />
      {open && results.length > 0 && (
        <ul className="calendar-location-picker-results">
          {results.map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => handleSelect(r)}>
                <span className="calendar-location-picker-name">{r.place_name}</span>
                <span className="calendar-location-picker-addr">{r.road_address_name || r.address_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {selectedPos && <LocationPreviewMap pos={selectedPos} />}
    </div>
  );
}
